import { Link } from "react-router";
import styled from "styled-components";

import { btcToString, truncateHash, fmtUsd } from "./utils";
import { exchangeFlowOf } from "./exchangeFlow";
import { TELEGRAM_HANDLE, TELEGRAM_URL } from "./telegram";

// Thresholds in BTC. Most transactions are dust, so without a floor a "large
// transfers" panel is just the feed again.
export { exchangeFlowOf };

export const WHALE_THRESHOLDS = [10, 50, 100];

const Panel = styled.section`
  border: 1px solid #3f3f44;
  border-left: 3px solid #f5a623;
  border-radius: 4px;
  padding: 0.6rem 0.8rem;
  margin-bottom: 1rem;
`;

const Top = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.8rem;
  flex-wrap: wrap;
`;

const Heading = styled.h3`
  margin: 0 0 0.15rem 0;
  font-size: 0.95rem;
`;

const Thresholds = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
  font-size: 0.75rem;
  color: #a3abba;
`;

const ThresholdButton = styled.button`
  padding: 0 0.45rem;
  font-size: 0.75rem;
  background-color: ${({ $active }) => ($active ? "#3f3f44" : "transparent")};
  color: ${({ $active }) => ($active ? "#fff" : "#a3abba")};
  border: 1px solid #3f3f44;
  border-radius: 3px;
  cursor: pointer;
`;

const Explainer = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 0.75rem;
  color: #a3abba;
  max-width: 80ch;
`;

// The channel posts the same events this panel lists, so this is where someone
// already reading whale transfers finds out they can be pushed one. It is a
// line of its own rather than a sentence in the explainer above because a link
// at the end of a paragraph nobody finishes is a link nobody follows.
//
// "The biggest of these" and not "these": the bot has a floor the panel does
// not, caps the day, and drops a hop whose coins it has already announced, so
// the channel is a subset of this list and the copy should not promise
// otherwise. README - Whale alerts has the rules.
const Alerts = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 0.75rem;
  color: #a3abba;

  a {
    color: var(--primary-color);
  }
`;

// Exchange flow gets its own row because it is the number people actually watch,
// and because it is cumulative for the session rather than a property of any one
// transfer in the list below.
//
// It stays session-only even though the list beside it is seeded from recent
// blocks. The list is about events, which happened whether or not anyone was
// watching; a running total is about a window, and quietly folding half an hour
// of history into one labelled "this session" would overstate it.
const Flow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem 1.4rem;
  padding: 0.4rem 0;
  margin-bottom: 0.2rem;
  border-top: 1px dotted #3f3f44;
  border-bottom: 1px dotted #3f3f44;
  font-size: 0.8rem;
`;

const FlowItem = styled.span`
  color: #a3abba;

  b {
    color: ${({ $tone }) =>
      $tone === "in" ? "#ff3c74" : $tone === "out" ? "#26a65b" : "#e8e9ed"};
    font-weight: normal;
    margin-left: 0.3rem;
  }
`;

const Empty = styled.p`
  margin: 0.3rem 0 0;
  font-size: 0.8rem;
  color: #a3abba;
`;

const Move = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.15rem 0.6rem;
  padding: 0.28rem 0;
  border-top: 1px dotted #3f3f44;
  font-size: 0.85rem;
`;

const Amount = styled.span`
  color: #f5a623;
  white-space: nowrap;
`;

const Usd = styled.span`
  color: #a3abba;
  white-space: nowrap;
`;

const When = styled.span`
  color: #a3abba;
  white-space: nowrap;
`;

// Only shown when one side of the transfer is a known exchange wallet. That is
// the whole point of the panel: 400 BTC moving between two unknown wallets is
// noise, and 400 BTC landing on an exchange is somebody preparing to sell.
const FlowTag = styled(Link)`
  margin-left: auto;
  padding: 0 0.35rem;
  border-radius: 3px;
  font-size: 0.75rem;
  white-space: nowrap;
  text-decoration: none;
  border: 1px solid ${({ $tone }) => ($tone === "in" ? "#ff3c74" : "#26a65b")};
  color: ${({ $tone }) => ($tone === "in" ? "#ff3c74" : "#26a65b")};
`;

const fmtTime = (date) =>
  date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const fmtBigUsd = (btc, price) => {
  if (!price) return null;
  const usd = btc * price;
  if (usd >= 1e9) return `$${(usd / 1e9).toFixed(2)}B`;
  if (usd >= 1e6) return `$${(usd / 1e6).toFixed(1)}M`;
  if (usd >= 1e3) return `$${Math.round(usd / 1e3)}K`;
  return `$${fmtUsd(usd)}`;
};


const SHOWN = 6;

const WhaleWatch = ({ whales, flow, minBtc, setMinBtc, btcPrice }) => {
  const visible = whales.filter((tx) => tx.netMovedBtc >= minBtc).slice(0, SHOWN);
  const net = flow.inBtc - flow.outBtc;
  const sawFlow = flow.inBtc > 0 || flow.outBtc > 0;

  return (
    <Panel>
      <Top>
        <Heading>Whale watch</Heading>
        <Thresholds>
          <span>over</span>
          {WHALE_THRESHOLDS.map((threshold) => (
            <ThresholdButton
              key={threshold}
              type="button"
              $active={minBtc === threshold}
              onClick={() => setMinBtc(threshold)}
            >
              {threshold}
            </ThresholdButton>
          ))}
          <span>BTC</span>
        </Thresholds>
      </Top>

      <Explainer>
        Large transfers, starting with the last few blocks and continuing live.
        Where one side is a wallet from
        the <Link to="/btc-explorer">notable list</Link>, the direction is
        shown — coins moving onto an exchange read very differently from coins
        moving off it. Coins usually travel in hops, so the same amount can
        appear here more than once as it moves from one address to the next.
      </Explainer>

      <Alerts>
        The biggest of these are posted as they confirm —{" "}
        <a href={TELEGRAM_URL} target="_blank" rel="noreferrer noopener">
          {TELEGRAM_HANDLE} on Telegram
        </a>
        .
      </Alerts>

      {sawFlow && (
        <Flow>
          <FlowItem $tone="in">
            Onto exchanges<b>{btcToString(flow.inBtc)} BTC</b>
          </FlowItem>
          <FlowItem $tone="out">
            Off exchanges<b>{btcToString(flow.outBtc)} BTC</b>
          </FlowItem>
          <FlowItem>
            Net<b>
              {net >= 0 ? "+" : "−"}
              {btcToString(Math.abs(net))} BTC
            </b>
          </FlowItem>
        </Flow>
      )}

      {visible.length === 0 ? (
        <Empty>
          Nothing over {minBtc} BTC in the last few blocks. Transfers this size are
          uncommon — a few an hour.
        </Empty>
      ) : (
        visible.map((tx) => {
          const usd = fmtBigUsd(tx.netMovedBtc, btcPrice);
          return (
            <Move key={tx.hash}>
              <Amount>{btcToString(tx.netMovedBtc)} BTC</Amount>
              {usd && <Usd>{usd}</Usd>}
              <When>
                {/* Every transaction in a block carries the block's one
                    timestamp, so seeded rows all read the same second and looked
                    like repeated data. The block is the truer label for them. */}
                {tx.seeded
                  ? `block ${tx.height.toLocaleString()}`
                  : fmtTime(tx.time)}{" "}
                ·{" "}
                <Link to={`/btc-explorer/${tx.hash}`}>
                  {truncateHash(tx.hash)}
                </Link>
              </When>
              {tx.flow && tx.flow.tone !== "internal" && (
                <FlowTag
                  $tone={tx.flow.tone}
                  to={`/btc-explorer/${tx.flow.tag.address}`}
                >
                  {tx.flow.tone === "in" ? "onto" : "off"} {tx.flow.tag.label}
                </FlowTag>
              )}
            </Move>
          );
        })
      )}
    </Panel>
  );
};

export default WhaleWatch;
